import type { ChangeOperation, SyncDelta, SyncedTableName } from './types.js';

export type SqlExecutor = {
  run(sql: string, params?: unknown[]): Promise<void>;
};

type ColumnDef = { column: string; from: string; kind?: 'json' | 'bool' | 'date' | 'number' };

const TABLE_COLUMNS: Record<SyncedTableName, ColumnDef[]> = {
  members: [
    { column: 'id', from: 'id' },
    { column: 'display_name', from: 'displayName' },
    { column: 'avatar_url', from: 'avatarUrl' },
    { column: 'telegram_user_id', from: 'telegramUserId' },
    { column: 'role', from: 'role' },
    { column: 'created_at', from: 'createdAt', kind: 'date' },
    { column: 'updated_at', from: 'updatedAt', kind: 'date' },
    { column: 'vector_clock', from: 'vectorClock', kind: 'json' },
    { column: 'sync_version', from: 'syncVersion', kind: 'number' },
  ],
  blocks: [
    { column: 'id', from: 'id' },
    { column: 'parent_id', from: 'parentId' },
    { column: 'position', from: 'position', kind: 'number' },
    { column: 'type', from: 'type' },
    { column: 'title', from: 'title' },
    { column: 'content', from: 'content', kind: 'json' },
    { column: 'icon', from: 'icon' },
    { column: 'created_at', from: 'createdAt', kind: 'date' },
    { column: 'updated_at', from: 'updatedAt', kind: 'date' },
    { column: 'deleted_at', from: 'deletedAt', kind: 'date' },
    { column: 'created_by', from: 'createdBy' },
    { column: 'updated_by', from: 'updatedBy' },
    { column: 'actor_kind', from: 'actorKind' },
    { column: 'vector_clock', from: 'vectorClock', kind: 'json' },
    { column: 'sync_version', from: 'syncVersion', kind: 'number' },
  ],
  block_properties: [
    { column: 'id', from: 'id' },
    { column: 'block_id', from: 'blockId' },
    { column: 'property_id', from: 'propertyId' },
    { column: 'value', from: 'value', kind: 'json' },
    { column: 'created_at', from: 'createdAt', kind: 'date' },
    { column: 'updated_at', from: 'updatedAt', kind: 'date' },
    { column: 'updated_by', from: 'updatedBy' },
    { column: 'actor_kind', from: 'actorKind' },
    { column: 'vector_clock', from: 'vectorClock', kind: 'json' },
    { column: 'sync_version', from: 'syncVersion', kind: 'number' },
  ],
  databases: [
    { column: 'id', from: 'id' },
    { column: 'block_id', from: 'blockId' },
    { column: 'created_at', from: 'createdAt', kind: 'date' },
    { column: 'updated_at', from: 'updatedAt', kind: 'date' },
    { column: 'vector_clock', from: 'vectorClock', kind: 'json' },
    { column: 'sync_version', from: 'syncVersion', kind: 'number' },
  ],
  database_schemas: [
    { column: 'id', from: 'id' },
    { column: 'database_id', from: 'databaseId' },
    { column: 'columns', from: 'columns', kind: 'json' },
    { column: 'schema_version', from: 'schemaVersion', kind: 'number' },
    { column: 'created_at', from: 'createdAt', kind: 'date' },
    { column: 'updated_at', from: 'updatedAt', kind: 'date' },
    { column: 'updated_by', from: 'updatedBy' },
    { column: 'actor_kind', from: 'actorKind' },
    { column: 'vector_clock', from: 'vectorClock', kind: 'json' },
    { column: 'sync_version', from: 'syncVersion', kind: 'number' },
  ],
  relations: [
    { column: 'id', from: 'id' },
    { column: 'from_block_id', from: 'fromBlockId' },
    { column: 'to_block_id', from: 'toBlockId' },
    { column: 'kind', from: 'kind' },
    { column: 'created_at', from: 'createdAt', kind: 'date' },
    { column: 'vector_clock', from: 'vectorClock', kind: 'json' },
    { column: 'sync_version', from: 'syncVersion', kind: 'number' },
  ],
  block_acl: [
    { column: 'id', from: 'id' },
    { column: 'block_id', from: 'blockId' },
    { column: 'member_id', from: 'memberId' },
    { column: 'can_read', from: 'canRead', kind: 'bool' },
    { column: 'can_write', from: 'canWrite', kind: 'bool' },
    { column: 'created_at', from: 'createdAt', kind: 'date' },
    { column: 'updated_at', from: 'updatedAt', kind: 'date' },
    { column: 'vector_clock', from: 'vectorClock', kind: 'json' },
    { column: 'sync_version', from: 'syncVersion', kind: 'number' },
  ],
  file_objects: [
    { column: 'id', from: 'id' },
    { column: 'block_id', from: 'blockId' },
    { column: 'object_key', from: 'objectKey' },
    { column: 'filename', from: 'filename' },
    { column: 'mime_type', from: 'mimeType' },
    { column: 'size_bytes', from: 'sizeBytes', kind: 'number' },
    { column: 'created_at', from: 'createdAt', kind: 'date' },
    { column: 'created_by', from: 'createdBy' },
    { column: 'vector_clock', from: 'vectorClock', kind: 'json' },
    { column: 'sync_version', from: 'syncVersion', kind: 'number' },
  ],
};

function coerceValue(value: unknown, kind?: ColumnDef['kind']): unknown {
  if (value === undefined) {
    return null;
  }
  if (value === null) {
    return null;
  }
  switch (kind) {
    case 'json':
      return JSON.stringify(value);
    case 'bool':
      return value ? 1 : 0;
    case 'date':
      if (value instanceof Date) {
        return value.getTime();
      }
      if (typeof value === 'string') {
        return Date.parse(value);
      }
      if (typeof value === 'number') {
        return value;
      }
      return null;
    case 'number':
      return typeof value === 'number' ? value : Number(value);
    default:
      return value;
  }
}

function rowValues(
  table: SyncedTableName,
  payload: Record<string, unknown>,
): { columns: string[]; values: unknown[] } {
  const defs = TABLE_COLUMNS[table];
  const columns: string[] = [];
  const values: unknown[] = [];
  for (const def of defs) {
    columns.push(def.column);
    let value = coerceValue(payload[def.from], def.kind);
    // Some change-event payloads omit columns the local schema declares NOT NULL
    // (e.g. file_objects/block_properties were historically recorded without a
    // vector_clock or timestamps). Insert a benign default instead of NULL so a
    // single under-specified row can't fail the INSERT and wedge sync forever.
    if (value === null) {
      if (def.column === 'vector_clock') {
        value = '{}';
      } else if (def.kind === 'date' && (def.column === 'created_at' || def.column === 'updated_at')) {
        value = Date.now();
      }
    }
    values.push(value);
  }
  return { columns, values };
}

export async function applyChange(
  exec: SqlExecutor,
  delta: SyncDelta,
): Promise<void> {
  const table = delta.table;
  const tableName = table;

  if (delta.operation === 'delete' && !delta.payload) {
    await exec.run(`DELETE FROM ${tableName} WHERE id = ?`, [delta.rowId]);
    return;
  }

  const payload = delta.payload ?? { id: delta.rowId };
  const { columns, values } = rowValues(table, payload);
  const placeholders = columns.map(() => '?').join(', ');
  await exec.run(
    `INSERT OR REPLACE INTO ${tableName} (${columns.join(', ')}) VALUES (${placeholders})`,
    values,
  );
}

export async function applyChanges(
  exec: SqlExecutor,
  deltas: SyncDelta[],
): Promise<void> {
  for (const delta of deltas) {
    await applyChange(exec, delta);
  }
}

export async function clearSyncedTables(exec: SqlExecutor): Promise<void> {
  const tables: SyncedTableName[] = [
    'file_objects',
    'block_acl',
    'relations',
    'database_schemas',
    'databases',
    'block_properties',
    'blocks',
    'members',
  ];
  for (const table of tables) {
    await exec.run(`DELETE FROM ${table}`);
  }
}

export function buildClientChangeFromPayload(
  table: SyncedTableName,
  operation: ChangeOperation,
  payload: Record<string, unknown>,
  baseSyncVersion: number | null,
): { table: SyncedTableName; operation: ChangeOperation; row: Record<string, unknown>; baseSyncVersion: number | null } {
  return { table, operation, row: payload, baseSyncVersion };
}
